import { haversine } from "./geo";
import type { OsmElement, OsmWay } from "./osm";
import type { MatchedTrail } from "./trails";
import { encodeFlags, encodeHighway, encodeSurface, isRunnable } from "./tags";

/**
 * Undirected walk graph on typed arrays (see docs/ARCHITECTURE.md → Graph).
 * Edge geometry runs u → v and includes both endpoints.
 */
export interface Graph {
  nodeCount: number;
  edgeCount: number;
  lat: Float64Array;
  lon: Float64Array;
  /** Global node id (OSM node id). */
  osmId: Float64Array;
  ele: Float32Array;

  u: Uint32Array;
  v: Uint32Array;
  len: Float32Array;
  climbUV: Float32Array;
  climbVU: Float32Array;
  pop: Float32Array;
  /** Share of the edge (0..1) in or near parks, forest or water. */
  green: Float32Array;
  surface: Uint8Array;
  highway: Uint8Array;
  flags: Uint8Array;
  wayId: Float64Array;

  geomStart: Uint32Array;
  geomLat: Float64Array;
  geomLon: Float64Array;

  /**
   * Running trails (ADR 0006), set by `applyTrails`; like `pop`, never serialized. `trail`: the
   * kind (0 none), `trailS`: strength × coverage (0..1), `trailId`: index into `trails` (-1 none).
   */
  trail: Uint8Array;
  trailS: Float32Array;
  trailId: Int32Array;
  trails: MatchedTrail[];

  /** CSR adjacency: edges incident to node n are adjEdge[adjStart[n] .. adjStart[n+1]). */
  adjStart: Uint32Array;
  adjEdge: Uint32Array;
}

export function otherEnd(g: Graph, e: number, n: number): number {
  return (g.u[e] as number) === n ? (g.v[e] as number) : (g.u[e] as number);
}

/** Open-addressing hash map from a float64 id to a dense index (0, 1, 2… in first-seen order). */
class IdIndex {
  private readonly keys: Float64Array;
  private readonly vals: Int32Array;
  private readonly mask: number;
  size = 0;
  constructor(expected: number) {
    let cap = 16;
    while (cap < expected * 2) cap *= 2;
    this.keys = new Float64Array(cap);
    this.vals = new Int32Array(cap).fill(-1);
    this.mask = cap - 1;
  }
  private slot(id: number): number {
    F64[0] = id === 0 ? 0 : id; // -0 and 0 are one key, as in a Map
    let h = Math.imul((U32[0] as number) ^ Math.imul(U32[1] as number, 0x9e3779b1), 0x85ebca6b);
    h ^= h >>> 15;
    let i = h & this.mask;
    while ((this.vals[i] as number) >= 0 && this.keys[i] !== id) i = (i + 1) & this.mask;
    return i;
  }
  /** Index of `id`, or -1. */
  get(id: number): number {
    return this.vals[this.slot(id)] as number;
  }
  /** Index of `id`, assigning the next one when new. Capacity is fixed: at most `expected` ids. */
  intern(id: number): number {
    const i = this.slot(id);
    const v = this.vals[i] as number;
    if (v >= 0) return v;
    this.keys[i] = id;
    this.vals[i] = this.size;
    return this.size++;
  }
}
const F64 = new Float64Array(1);
const U32 = new Uint32Array(F64.buffer);

/** Growable Int32Array. `n` may be lowered to truncate. */
class I32s {
  a: Int32Array;
  n = 0;
  constructor(cap = 1024) {
    this.a = new Int32Array(cap);
  }
  push(x: number): void {
    if (this.n === this.a.length) {
      const b = new Int32Array(this.a.length * 2);
      b.set(this.a);
      this.a = b;
    }
    this.a[this.n++] = x;
  }
}

/** Growable Float64Array. */
class F64s {
  a: Float64Array;
  n = 0;
  constructor(cap = 1024) {
    this.a = new Float64Array(cap);
  }
  push(x: number): void {
    if (this.n === this.a.length) {
      const b = new Float64Array(this.a.length * 2);
      b.set(this.a);
      this.a = b;
    }
    this.a[this.n++] = x;
  }
}

/** Raw (pre-component) edges as struct-of-arrays; endpoints and geometry are coordinate indices. */
interface RawEdges {
  a: I32s;
  b: I32s;
  len: F64s;
  /** Index into the runnable ways. */
  way: I32s;
  /** Geometry: `count` coordinate indices from `geomAt` in the shared index buffer. */
  geomAt: I32s;
  count: I32s;
}

/**
 * Build the runnable graph from Overpass elements; keeps only the largest connected component.
 * Typed arrays throughout (ids → dense indices via a hash table, flat run and geometry buffers),
 * so peak memory stays low at the tile cap. Output is identical to the original Map-based build
 * (`test/graphReference.ts`).
 */
export function buildGraph(elements: readonly OsmElement[]): Graph {
  // Runnable ways by id, in first-seen order; a repeated id keeps its slot but the last way wins.
  let nNodes = 0;
  let nWays = 0;
  for (const el of elements) {
    if (el.type === "node") nNodes++;
    else if (el.type === "way" && el.nodes) nWays++;
  }
  const wayIx = new IdIndex(nWays);
  const ways: OsmWay[] = [];
  const nodeIx = new IdIndex(nNodes);
  const lat = new Float64Array(nNodes);
  const lon = new Float64Array(nNodes);
  const osm = new Float64Array(nNodes);
  for (const el of elements) {
    if (el.type === "node") {
      const i = nodeIx.intern(el.id);
      lat[i] = el.lat;
      lon[i] = el.lon;
      osm[i] = el.id;
    } else if (el.type === "way" && el.nodes && isRunnable(el.tags)) {
      ways[wayIx.intern(el.id)] = el;
    }
  }

  // Split each way into runs of nodes we have coordinates for, dropping consecutive duplicates.
  const runNodes = new I32s(Math.max(1024, nNodes));
  const runStart = new I32s();
  const runWay = new I32s();
  for (let w = 0; w < ways.length; w++) {
    let start = runNodes.n;
    let last = -1;
    for (const id of (ways[w] as OsmWay).nodes ?? []) {
      const c = nodeIx.get(id);
      if (c < 0) {
        if (runNodes.n - start >= 2) {
          runStart.push(start);
          runWay.push(w);
        } else runNodes.n = start;
        start = runNodes.n;
        last = -1;
      } else if (c !== last) {
        runNodes.push(c);
        last = c;
      }
    }
    if (runNodes.n - start >= 2) {
      runStart.push(start);
      runWay.push(w);
    } else runNodes.n = start;
  }
  const nRuns = runStart.n;
  runStart.push(runNodes.n);
  const rn = runNodes.a;
  const rs = runStart.a;

  // Vertices: run endpoints, nodes shared by several runs or repeated in a run, and the middle of
  // closed runs (so a closed way never becomes a self-loop edge).
  const uses = new Uint8Array(nNodes);
  const vertex = new Uint8Array(nNodes);
  for (let r = 0; r < nRuns; r++) {
    const s = rs[r] as number;
    const t = rs[r + 1] as number;
    const first = rn[s] as number;
    const last = rn[t - 1] as number;
    vertex[first] = 1;
    vertex[last] = 1;
    if (first === last && t - s > 2) vertex[rn[s + ((t - s) >> 1)] as number] = 1;
    for (let i = s; i < t; i++) {
      const c = rn[i] as number;
      if ((uses[c] as number) < 2) uses[c] = (uses[c] as number) + 1;
    }
  }
  for (let c = 0; c < nNodes; c++) if ((uses[c] as number) > 1) vertex[c] = 1;

  const geom = new I32s(Math.max(1024, runNodes.n + nRuns));
  const raw: RawEdges = {
    a: new I32s(),
    b: new I32s(),
    len: new F64s(),
    way: new I32s(),
    geomAt: new I32s(),
    count: new I32s(),
  };
  const add = (a: number, b: number, len: number, w: number, at: number, count: number) => {
    raw.a.push(a);
    raw.b.push(b);
    raw.len.push(len);
    raw.way.push(w);
    raw.geomAt.push(at);
    raw.count.push(count);
  };
  const polyLen = (at: number, count: number): number => {
    let len = 0;
    const ga = geom.a;
    for (let i = at + 1; i < at + count; i++) {
      const p = ga[i - 1] as number;
      const q = ga[i] as number;
      len += haversine(lat[p] as number, lon[p] as number, lat[q] as number, lon[q] as number);
    }
    return len;
  };
  for (let r = 0; r < nRuns; r++) {
    const w = runWay.a[r] as number;
    const s = rs[r] as number;
    const t = rs[r + 1] as number;
    let a = rn[s] as number;
    let at = geom.n;
    let len = 0;
    let prev = -1;
    for (let i = s; i < t; i++) {
      const c = rn[i] as number;
      if (prev >= 0)
        len += haversine(
          lat[prev] as number,
          lon[prev] as number,
          lat[c] as number,
          lon[c] as number,
        );
      geom.push(c);
      prev = c;
      const count = geom.n - at;
      if (!vertex[c] || count < 2) continue;
      let kept = false;
      if (c !== a) {
        if (len > 0) {
          add(a, c, len, w, at, count);
          kept = true;
        }
      } else if (count > 2) {
        // The way came back to `a` (e.g. A-B-C-D-B-E): split the loop at its middle node so it
        // becomes two real edges instead of being folded into the next one.
        const m = count >> 1;
        const mid = geom.a[at + m] as number;
        add(a, mid, polyLen(at, m + 1), w, at, m + 1);
        add(mid, a, polyLen(at + m, count - m), w, at + m, count - m);
        kept = true;
      }
      // The next edge starts at this vertex; geometry of a dropped edge is reused.
      if (!kept) geom.n = at;
      at = geom.n;
      geom.push(c);
      a = c;
      len = 0;
    }
    geom.n = at;
  }

  return finalize(raw, ways, geom.a, lat, lon, osm);
}

function finalize(
  raw: RawEdges,
  ways: readonly OsmWay[],
  geomIx: Int32Array,
  lat: Float64Array,
  lon: Float64Array,
  osm: Float64Array,
): Graph {
  const nRaw = raw.a.n;
  // Dense ids for every vertex that has an edge, in first-seen order (a, then b, per edge).
  const dense = new Int32Array(lat.length).fill(-1);
  const ids = new I32s();
  const idx = (c: number): number => {
    let i = dense[c] as number;
    if (i < 0) {
      i = ids.n;
      dense[c] = i;
      ids.push(c);
    }
    return i;
  };
  const endA = new Int32Array(nRaw);
  const endB = new Int32Array(nRaw);
  for (let e = 0; e < nRaw; e++) {
    endA[e] = idx(raw.a.a[e] as number);
    endB[e] = idx(raw.b.a[e] as number);
  }
  const n = ids.n;

  // Largest connected component (union-find); ties go to the component seen first.
  const parent = new Int32Array(n).map((_, i) => i);
  const find = (x: number): number => {
    while (parent[x] !== x) {
      const p = parent[x] as number;
      parent[x] = parent[p] as number;
      x = p;
    }
    return x;
  };
  for (let e = 0; e < nRaw; e++) {
    const ra = find(endA[e] as number);
    const rb = find(endB[e] as number);
    if (ra !== rb) parent[ra] = rb;
  }
  const size = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const r = find(i);
    size[r] = (size[r] as number) + 1;
  }
  let best = -1;
  let bestSize = 0;
  const seen = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (seen[r]) continue;
    seen[r] = 1;
    if ((size[r] as number) > bestSize) {
      best = r;
      bestSize = size[r] as number;
    }
  }

  // Re-index the kept component.
  const keep = new Int32Array(n).fill(-1);
  let nodeCount = 0;
  for (let i = 0; i < n; i++) if (find(i) === best) keep[i] = nodeCount++;
  const kept = new I32s();
  for (let e = 0; e < nRaw; e++) if ((keep[endA[e] as number] as number) >= 0) kept.push(e);

  const edgeCount = kept.n;
  const g: Graph = {
    nodeCount,
    edgeCount,
    lat: new Float64Array(nodeCount),
    lon: new Float64Array(nodeCount),
    osmId: new Float64Array(nodeCount),
    ele: new Float32Array(nodeCount),
    u: new Uint32Array(edgeCount),
    v: new Uint32Array(edgeCount),
    len: new Float32Array(edgeCount),
    climbUV: new Float32Array(edgeCount),
    climbVU: new Float32Array(edgeCount),
    pop: new Float32Array(edgeCount),
    trail: new Uint8Array(edgeCount),
    trailS: new Float32Array(edgeCount),
    trailId: new Int32Array(edgeCount).fill(-1),
    trails: [],
    green: new Float32Array(edgeCount),
    surface: new Uint8Array(edgeCount),
    highway: new Uint8Array(edgeCount),
    flags: new Uint8Array(edgeCount),
    wayId: new Float64Array(edgeCount),
    geomStart: new Uint32Array(edgeCount + 1),
    geomLat: new Float64Array(0),
    geomLon: new Float64Array(0),
    adjStart: new Uint32Array(nodeCount + 1),
    adjEdge: new Uint32Array(edgeCount * 2),
  };
  for (let i = 0; i < n; i++) {
    const k = keep[i] as number;
    if (k < 0) continue;
    const c = ids.a[i] as number;
    g.lat[k] = lat[c] as number;
    g.lon[k] = lon[c] as number;
    g.osmId[k] = osm[c] as number;
  }

  // Tag codes once per way.
  const hw = new Uint8Array(ways.length);
  const sf = new Uint8Array(ways.length);
  const fl = new Uint8Array(ways.length);
  for (let w = 0; w < ways.length; w++) {
    const tags = (ways[w] as OsmWay).tags ?? {};
    hw[w] = encodeHighway(tags);
    sf[w] = encodeSurface(tags);
    fl[w] = encodeFlags(tags);
  }

  let geomTotal = 0;
  for (let j = 0; j < edgeCount; j++) geomTotal += raw.count.a[kept.a[j] as number] as number;
  g.geomLat = new Float64Array(geomTotal);
  g.geomLon = new Float64Array(geomTotal);

  let gp = 0;
  const degree = new Uint32Array(nodeCount);
  for (let j = 0; j < edgeCount; j++) {
    const e = kept.a[j] as number;
    const w = raw.way.a[e] as number;
    const u = keep[endA[e] as number] as number;
    const v = keep[endB[e] as number] as number;
    g.u[j] = u;
    g.v[j] = v;
    g.len[j] = raw.len.a[e] as number;
    g.highway[j] = hw[w] as number;
    g.surface[j] = sf[w] as number;
    g.flags[j] = fl[w] as number;
    g.wayId[j] = (ways[w] as OsmWay).id;
    g.geomStart[j] = gp;
    const at = raw.geomAt.a[e] as number;
    const end = at + (raw.count.a[e] as number);
    for (let i = at; i < end; i++) {
      const c = geomIx[i] as number;
      g.geomLat[gp] = lat[c] as number;
      g.geomLon[gp] = lon[c] as number;
      gp++;
    }
    degree[u] = (degree[u] as number) + 1;
    if (v !== u) degree[v] = (degree[v] as number) + 1;
  }
  g.geomStart[edgeCount] = gp;

  for (let k = 0; k < nodeCount; k++) {
    g.adjStart[k + 1] = (g.adjStart[k] as number) + (degree[k] as number);
  }
  const fill = g.adjStart.slice(0, nodeCount);
  for (let e = 0; e < edgeCount; e++) {
    const u = g.u[e] as number;
    const v = g.v[e] as number;
    g.adjEdge[fill[u] as number] = e;
    fill[u] = (fill[u] as number) + 1;
    if (v !== u) {
      g.adjEdge[fill[v] as number] = e;
      fill[v] = (fill[v] as number) + 1;
    }
  }
  return g;
}

/** Edge polyline as [lat, lon] pairs, oriented from `from` (must be u or v). */
export function edgeCoords(g: Graph, e: number, from: number): [number, number][] {
  const s = g.geomStart[e] as number;
  const t = g.geomStart[e + 1] as number;
  const pts: [number, number][] = [];
  for (let i = s; i < t; i++) pts.push([g.geomLat[i] as number, g.geomLon[i] as number]);
  if (from !== (g.u[e] as number)) pts.reverse();
  return pts;
}

// ---- Persistence (IndexedDB graph store, sources/graphstore.ts) ---------------------------------

/** "MATG" little-endian. */
const GRAPH_MAGIC = 0x4754414d;
/** Bump when the layout, or anything that changes a built graph (tags, omt, green), changes. */
export const GRAPH_FORMAT_VERSION = 2;
const HEADER_U32 = 8; // magic, version, nodeCount, edgeCount, geomCount, adjCount, 0, 0

/** Not persisted: `pop` (from traces) and the trail arrays (from trail cells, ADR 0006). */
type Transient = "pop" | "trail" | "trailS" | "trailId" | "trails";
type ArrayKey = Exclude<keyof Graph, "nodeCount" | "edgeCount" | Transient>;
type Ctor =
  | Float64ArrayConstructor
  | Float32ArrayConstructor
  | Uint32ArrayConstructor
  | Uint8ArrayConstructor;
type Count = "node" | "edge" | "edge1" | "node1" | "geom" | "adj";

/** Section order: widest element first, so every section starts aligned without padding gaps. */
const SECTIONS: readonly (readonly [ArrayKey, Ctor, Count])[] = [
  ["lat", Float64Array, "node"],
  ["lon", Float64Array, "node"],
  ["osmId", Float64Array, "node"],
  ["wayId", Float64Array, "edge"],
  ["geomLat", Float64Array, "geom"],
  ["geomLon", Float64Array, "geom"],
  ["ele", Float32Array, "node"],
  ["len", Float32Array, "edge"],
  ["climbUV", Float32Array, "edge"],
  ["climbVU", Float32Array, "edge"],
  ["green", Float32Array, "edge"],
  ["u", Uint32Array, "edge"],
  ["v", Uint32Array, "edge"],
  ["geomStart", Uint32Array, "edge1"],
  ["adjStart", Uint32Array, "node1"],
  ["adjEdge", Uint32Array, "adj"],
  ["surface", Uint8Array, "edge"],
  ["highway", Uint8Array, "edge"],
  ["flags", Uint8Array, "edge"],
];

const align8 = (n: number) => (n + 7) & ~7;

function layout(c: Record<Count, number>): { offsets: number[]; total: number } {
  let p = HEADER_U32 * 4;
  const offsets: number[] = [];
  for (const [, ctor, count] of SECTIONS) {
    p = align8(p);
    offsets.push(p);
    p += c[count] * ctor.BYTES_PER_ELEMENT;
  }
  return { offsets, total: align8(p) };
}

const counts = (node: number, edge: number, geom: number, adj: number): Record<Count, number> => ({
  node,
  edge,
  node1: node + 1,
  edge1: edge + 1,
  geom,
  adj,
});

/**
 * One ArrayBuffer holding every typed array of `g` except `pop` (recomputed from traces) and the
 * trail arrays (re-matched from trail cells, ADR 0006), behind
 * a 32-byte header (magic, version, counts). Sections are 8-byte aligned, so `deserializeGraph`
 * can return zero-copy views.
 */
export function serializeGraph(g: Graph): ArrayBuffer {
  const c = counts(g.nodeCount, g.edgeCount, g.geomLat.length, g.adjEdge.length);
  const { offsets, total } = layout(c);
  const buf = new ArrayBuffer(total);
  new Uint32Array(buf, 0, HEADER_U32).set([
    GRAPH_MAGIC,
    GRAPH_FORMAT_VERSION,
    c.node,
    c.edge,
    c.geom,
    c.adj,
  ]);
  SECTIONS.forEach(([key, ctor, count], i) => {
    const src = g[key];
    if (src.length !== c[count]) throw new Error(`Graph array ${key} has the wrong length`);
    new ctor(buf, offsets[i], c[count]).set(src);
  });
  return buf;
}

/**
 * Graph from `serializeGraph` output. Arrays are views on the input's buffer when it is 8-byte
 * aligned (always for a plain ArrayBuffer); otherwise the bytes are copied once. `pop` and the trail
 * arrays are fresh (zeroed, `trailId` -1, no trails). Throws on a foreign, truncated or other-version buffer.
 */
export function deserializeGraph(data: ArrayBuffer | Uint8Array): Graph {
  let buf: ArrayBuffer;
  let base: number;
  if (data instanceof Uint8Array) {
    if (data.byteOffset % 8 === 0 && data.buffer instanceof ArrayBuffer) {
      buf = data.buffer;
      base = data.byteOffset;
    } else {
      buf = data.slice().buffer;
      base = 0;
    }
  } else {
    buf = data;
    base = 0;
  }
  const size = data.byteLength;
  if (size < HEADER_U32 * 4) throw new Error("Not a stored graph (too short)");
  const h = new Uint32Array(buf, base, HEADER_U32);
  if (h[0] !== GRAPH_MAGIC) throw new Error("Not a stored graph (bad magic)");
  if (h[1] !== GRAPH_FORMAT_VERSION) throw new Error(`Stored graph has version ${h[1]}`);
  const c = counts(h[2] as number, h[3] as number, h[4] as number, h[5] as number);
  const { offsets, total } = layout(c);
  if (total !== size) throw new Error("Stored graph is truncated or padded");
  const out: Partial<Record<ArrayKey, unknown>> = {};
  SECTIONS.forEach(([key, ctor, count], i) => {
    out[key] = new ctor(buf, base + (offsets[i] as number), c[count]);
  });
  const g = {
    ...(out as Pick<Graph, ArrayKey>),
    nodeCount: c.node,
    edgeCount: c.edge,
    pop: new Float32Array(c.edge),
    trail: new Uint8Array(c.edge),
    trailS: new Float32Array(c.edge),
    trailId: new Int32Array(c.edge).fill(-1),
    trails: [],
  };
  if (g.adjStart[c.node] !== c.adj || g.geomStart[c.edge] !== c.geom) {
    throw new Error("Stored graph is inconsistent");
  }
  return g;
}
